import * as React from 'react';
import classNames from 'classnames';
import { useField } from 'formik';
import { FormError, MultiSelect, MultiSelectDataItem, MultiSelectProps } from '../controls';

interface MultiSelectFieldProps extends Omit<MultiSelectProps, 'sourceItems' | 'showAddButton' | 'className' | 'onItemSelect'> {
    /** Formik field holding the selected values, unless selectedValues and onItemSelect are passed explicitly */
    name: string;
    label: string;
    /** Selectable items, without the selection state — it is derived from the selected values */
    dataItems: MultiSelectDataItem[];
    selectedValues?: Array<string | number>;
    /** Defaults to toggling the value in the Formik field */
    onItemSelect?: MultiSelectProps['onItemSelect'];
    errorMessage?: string;
    horizontalOrientation?: boolean;
    wrapperClassName?: string;
}

export function MultiSelectField({
    name,
    label,
    dataItems,
    selectedValues,
    errorMessage,
    horizontalOrientation = false,
    wrapperClassName,
    placeholder = 'Select',
    required = false,
    onItemSelect,
    onResetAll,
    onSelectAll,
    showResetButton = true,
    showSelectAllButton = false,
    ...multiSelectProps
}: MultiSelectFieldProps) {
    const [field, meta, helpers] = useField<Array<string | number> | undefined>(name);

    const values = selectedValues ?? field.value ?? [];
    const selected = new Set(values.map(String));

    // Kept sorted, so that the field value depends on what is selected and not on the order it was selected in
    const handleItemSelect: MultiSelectProps['onItemSelect'] = onItemSelect ?? ((item, isSelected) => helpers.setValue(
        (isSelected
            ? [...values, String(item.value)]
            : values.filter(v => String(v) !== String(item.value))
        ).sort(),
    ));

    const error = errorMessage ?? ((meta.touched && meta.error) || '');

    return (
        <div
            className={classNames('position-relative', wrapperClassName, { 'data-item-row': horizontalOrientation })}
            onClick={e => e.stopPropagation()}
        >
            {label && (
                <label className={classNames({
                    'data-item-col data-item-col-title': horizontalOrientation,
                    'form-label': !horizontalOrientation,
                })}>
                    {label}{required && <span className="text-red"> *</span>}
                </label>
            )}
            <div className={classNames({ 'data-item-col': horizontalOrientation })}>
                <MultiSelect
                    {...multiSelectProps}
                    className={classNames({ 'is-invalid': !!error })}
                    sourceItems={dataItems.map(i => ({ ...i, selected: selected.has(String(i.value)) }))}
                    showAddButton={false}
                    name={name}
                    placeholder={placeholder}
                    required={required}
                    onItemSelect={handleItemSelect}
                    onResetAll={onResetAll ?? (() => helpers.setValue([]))}
                    onSelectAll={onSelectAll ?? (() => helpers.setValue(
                        dataItems.filter(i => !i.disabled).map(i => String(i.value)).sort(),
                    ))}
                    showResetButton={showResetButton}
                    showSelectAllButton={showSelectAllButton}
                />
                <FormError message={error} />
            </div>
        </div>
    );
}
